// @flow
import React from 'react'
import _ from 'lodash'
import type { BriefData } from './BriefContainer'
import type { OptionType } from '../types'
import type { StyledComponent } from 'styled-components'
import styled from 'styled-components'
import CheckMark from '../controls/CheckMark'
import { useMediaQuery } from 'react-responsive'

type Props = {|
  adObjectType: 'site' | 'vk' | 'instagram',
  value: string,
  onChange: ($Shape<BriefData>) => void
|}

const budgetOptions: Array<OptionType> = [
  { id: '300', name: '300 ₽' },
  { id: '500', name: '500 ₽' },
  { id: '900', name: '900 ₽' }
]

const optionDescriptions = {
  site: {
    '300': { minVisits: 300, maxVisits: 900, minClients: 4, maxClients: 45 },
    '500': { minVisits: 560, maxVisits: 1400, minClients: 7, maxClients: 70 },
    '900': { minVisits: 1100, maxVisits: 2900, minClients: 14, maxClients: 145 }
  },
  other: {
    '300': { minSubs: 90, maxSubs: 180 },
    '500': { minSubs: 140, maxSubs: 280 },
    '900': { minSubs: 290, maxSubs: 580 }
  }
}

const Wrapper: StyledComponent<Object, any, any> = styled.div`
  display: flex;
  @media (max-width: 900px) {
    display: block;
    width: 100%;
  }
`
const ItemText: StyledComponent<Object, any, any> = styled.div`
  padding: 5px 10px;
`
const ItemTitle: StyledComponent<Object, any, any> = styled.div`
  text-align: center;
  font-size: 24px;
  width: 100%;
  @media (max-width: 900px) {
    text-align: left;
  }
`
const ItemNote: StyledComponent<Object, any, any> = styled.div`
  font-size: 13px;
  text-align: center;
  @media (max-width: 900px) {
    padding-top: 3px;
    text-align: left;
  }
`
const Item: StyledComponent<Object, any, any> = styled.div`
  border: 1px solid #ccc;
  border-radius: 5px;
  padding: 12px 0;
  position: relative;
  cursor: pointer;
  width: 200px;
  height: 150px;
  margin-right: 5px;
  margin-bottom: 5px;
  &:hover {
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
  }
  @media (max-width: 900px) {
    width: 100%;
    height: auto;
    padding: 10px 0;
  }
`
const CheckMarkWrapper = styled.div`
  position: absolute;
  @media (min-width: 901px) {
    top: 5px;
    left: 5px;
  }
  @media (max-width: 900px) {
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
  }
`
const Strong = styled.div`
  font-weight: bold;
`

export default function BriefBudgetSelect(props: Props) {
  const isNarrow = useMediaQuery({ maxWidth: 900 })

  return (
    <Wrapper>
      {_.map(budgetOptions, (option) => {
        const isActive = option.id === props.value
        return (
          <Item key={option.id} onClick={() => props.onChange({ budget: option.id })}>
            <CheckMarkWrapper>
              <CheckMark isActive={isActive} />
            </CheckMarkWrapper>
            <ItemText>
              <ItemTitle>
                {option.name}
                {isNarrow ? ' в день' : ''}
              </ItemTitle>
              <ItemNote>
                {!isNarrow && (
                  <div>
                    <div>в день</div>
                    <br />
                    <Strong>Вы получите:</Strong>
                    <br />
                  </div>
                )}
                {budgetOptionDescription(option.id, props.adObjectType)}
              </ItemNote>
            </ItemText>
          </Item>
        )
      })}
    </Wrapper>
  )
}

function budgetOptionDescription(optionId: string, adObjectType: 'site' | 'vk' | 'instagram') {
  return (
    <div>
      {_.map(resultProjection(optionId, adObjectType), (line, index) => (
        <div key={index}>{line}</div>
      ))}
    </div>
  )
}

export function resultProjection(
  optionId: string,
  adObjectType: 'site' | 'vk' | 'instagram'
): Array<string> {
  if (!_.includes(['site', 'vk', 'instagram'], adObjectType)) return ['Unknown ad object type']

  if (adObjectType === 'site') {
    const hash = optionDescriptions['site'][optionId]
    if (!hash) return ['Unknown option']
    return [
      `От ${hash.minVisits} до ${hash.maxVisits} посетитилей`,
      `От ${hash.minClients} до ${hash.maxClients} клиентов`
    ]
  } else {
    const hash = optionDescriptions['other'][optionId]
    if (!hash) return ['Unknown option']
    return [`От ${hash.minSubs} до ${hash.maxSubs} подписчиков`]
  }
}

export function translateBudgetOption(key: any) {
  const option = _.find(budgetOptions, (option) => option.id === `${key}`)

  return option ? option.name : `${key} ₽`
}
